/// <reference types="vite/client" />
import { combineReducers, configureStore, Reducer } from '@reduxjs/toolkit';
import { createBrowserHistory } from 'history';
import { createReduxHistoryContext } from 'redux-first-history';
import createSagaMiddleware from 'redux-saga';
import { thunk } from 'redux-thunk';
import { createFocusInvalidFieldMiddleware } from '@jjj/components';
import reducers from './reducers';
import rootSaga from './sagas';
import { trackPage } from '../helpers/googleAnalytics/events';
import { initOrgCodeSync } from '../settings/organizationSettings';
import { setStoreAccessor } from './storeAccessor';

const { createReduxHistory, routerMiddleware, routerReducer } = createReduxHistoryContext({
  history: createBrowserHistory(),
  reduxTravelling: true,
});

const sagaMiddleware = createSagaMiddleware();

const buildRootReducer = (modules: typeof reducers) =>
  combineReducers({ ...modules, router: routerReducer } as any) as unknown as Reducer;

const store = configureStore({
  reducer: buildRootReducer(reducers),
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      thunk: false,
      serializableCheck: false,
      immutableCheck: false,
    }).concat(sagaMiddleware, routerMiddleware, thunk, createFocusInvalidFieldMiddleware()),
});

setStoreAccessor(store);

export const history = createReduxHistory(store);

let prevPath: string | null = null;

const trackPathChanges = () => {
  const { pathname } = window.location;

  if (pathname !== prevPath) {
    prevPath = pathname;
    setTimeout(() => trackPage(), 1000);
  }
};

window.addEventListener('popstate', trackPathChanges);

let sagaTask = sagaMiddleware.run(rootSaga);
initOrgCodeSync(store);

if (import.meta.hot) {
  import.meta.hot.accept('./reducers', mod => {
    if (!mod) return;
    store.replaceReducer(buildRootReducer(mod.default));
  });

  import.meta.hot.accept('./sagas', mod => {
    if (!mod) return;
    sagaTask.cancel();
    sagaTask = sagaMiddleware.run(mod.default);
  });
}

export type RootState = ReturnType<typeof store.getState>;

export { store };
